import { z } from 'zod';
import { ACTOR_FILTERS, ACTOR_OPTIONS, AUDIT_KINDS, KIND_ACTIONS, isActorFilter } from './vocab';

export const AUDIT_PAGE_SIZE = 50;

const actor = z.union([z.enum(ACTOR_FILTERS), z.uuid()]);

/** The trail's view, kept in the URL: who, what kind, which days, and what it's about. */
export const auditSearchSchema = z.object({
  actor: actor.optional().catch(undefined),
  kind: z.enum(AUDIT_KINDS).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  record: z.uuid().optional().catch(undefined),
  source: z.uuid().optional().catch(undefined),
  group: z.uuid().optional().catch(undefined),
  event: z.uuid().optional().catch(undefined),
  run: z.uuid().optional().catch(undefined),
  rule: z.uuid().optional().catch(undefined),
  page: z.coerce.number().int().min(1).default(1).catch(1),
});

export type AuditSearch = z.output<typeof auditSearchSchema>;

const FILTERS = [
  'actor',
  'kind',
  'from',
  'to',
  'record',
  'source',
  'group',
  'event',
  'run',
  'rule',
] as const satisfies (keyof AuditSearch)[];

export const noFilters: Partial<AuditSearch> = Object.fromEntries(
  FILTERS.map((key) => [key, undefined]),
);

export const hasFilters = (search: AuditSearch): boolean =>
  FILTERS.some((key) => search[key] !== undefined);

export function toApiQuery(search: AuditSearch) {
  const actorKindFilter =
    search.actor !== undefined && isActorFilter(search.actor) ? search.actor : undefined;
  return {
    actorKind: actorKindFilter ? [...ACTOR_OPTIONS[actorKindFilter].kinds] : undefined,
    actorUserId: search.actor && !actorKindFilter ? search.actor : undefined,
    action: search.kind ? KIND_ACTIONS[search.kind] : undefined,
    from: search.from,
    to: search.to,
    stagedEventId: search.record,
    sourceId: search.source,
    clusterId: search.group,
    raceEventId: search.event,
    runId: search.run,
    ruleId: search.rule,
    pageNumber: search.page,
    pageSize: AUDIT_PAGE_SIZE,
  };
}
